import { motion } from 'framer-motion';

interface SpendingInsightMiniBoxProps {
  income: number;
  spending: number;
}

export const SpendingInsightMiniBox = ({ income, spending }: SpendingInsightMiniBoxProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate the ratio for the circular progress
  const ratio = spending / income;
  const percentage = Math.min(ratio * 100, 100);
  const isOverspending = spending > income;

  return (
    <div className="bg-white rounded-xl p-4 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]">
      <div className="text-[#221A51]/60 text-sm mb-3">Income vs Spending</div>
      <div className="flex items-center justify-between mb-4">
        <div className="relative w-16 h-16">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="28"
              stroke="#E5E7EB"
              strokeWidth="6"
              fill="none"
            />
            <motion.circle
              cx="32"
              cy="32"
              r="28"
              stroke={isOverspending ? "#FF6B6B" : "#9BFFA9"}
              strokeWidth="6"
              fill="none"
              strokeDasharray={175.93}
              initial={{ strokeDashoffset: 175.93 }}
              animate={{
                strokeDashoffset: 175.93 * (1 - percentage / 100)
              }}
              transition={{ duration: 1, delay: 0.2 }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-sm font-bold text-[#221A51]">
              {Math.round(percentage)}%
            </div>
          </div>
        </div>
        <div className="flex-1 ml-4">
          <div className="text-sm text-[#221A51]/60 mb-1">
            {isOverspending ? 'Overspent by' : 'Saved'}
          </div>
          <div className="text-xl font-bold text-[#221A51]">
            {formatCurrency(Math.abs(income - spending))}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-baseline">
          <div className="text-sm text-[#221A51]/60">Income</div>
          <div className="text-base font-semibold text-[#221A51]">
            {formatCurrency(income)}
          </div>
        </div>
        <div className="flex justify-between items-baseline">
          <div className="text-sm text-[#221A51]/60">Spending</div>
          <div className="text-base font-semibold text-[#221A51]">
            {formatCurrency(spending)}
          </div>
        </div>
      </div>
    </div>
  );
};
